import React, { useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';

const RANGES = [
  { key: '2h',  label: '2 Hours',  secs: 2  * 60 * 60 },
  { key: '6h',  label: '6 Hours',  secs: 6  * 60 * 60 },
  { key: '12h', label: '12 Hours', secs: 12 * 60 * 60 },
  { key: '24h', label: '24 Hours', secs: 24 * 60 * 60 },
];

const TEMP_WARN  = 30;
const TEMP_CRIT  = 45;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const temp = payload[0]?.value;
    const color = temp >= TEMP_CRIT ? '#ef4444' : temp >= TEMP_WARN ? '#f59e0b' : '#22d3ee';
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip-label">{label}</p>
        <p className="chart-tooltip-value" style={{ color }}>
          🌡️ {temp?.toFixed(1)}°C
        </p>
      </div>
    );
  }
  return null;
};

export default function TemperatureChart({ tempHistory }) {
  const [range, setRange] = useState('6h');
  const [selectedKiosk, setSelectedKiosk] = useState('all');

  const kiosks = useMemo(() => Object.keys(tempHistory || {}), [tempHistory]);

  const chartData = useMemo(() => {
    const rangeSecs = RANGES.find(r => r.key === range)?.secs || 21600;
    const cutoff = Date.now() / 1000 - rangeSecs;

    // Merge readings from selected kiosk(s)
    const readings = [];
    const sources = selectedKiosk === 'all' ? kiosks : [selectedKiosk];

    sources.forEach(kioskId => {
      const history = tempHistory[kioskId] || {};
      Object.values(history).forEach(entry => {
        if (entry.ts >= cutoff) {
          readings.push({ ts: entry.ts, t: entry.t, kiosk: kioskId });
        }
      });
    });

    readings.sort((a, b) => a.ts - b.ts);

    return readings.map(r => ({
      time: new Date(r.ts * 1000).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: false
      }),
      temp: r.t,
      ts: r.ts,
    }));
  }, [tempHistory, range, selectedKiosk, kiosks]);

  const latestTemp = chartData.length > 0 ? chartData[chartData.length - 1].temp : null;
  const maxTemp    = chartData.length > 0 ? Math.max(...chartData.map(d => d.temp)) : null;
  const minTemp    = chartData.length > 0 ? Math.min(...chartData.map(d => d.temp)) : null;

  const tempColor = latestTemp == null ? '#22d3ee'
    : latestTemp >= TEMP_CRIT ? '#ef4444'
    : latestTemp >= TEMP_WARN ? '#f59e0b'
    : '#22d3ee';

  if (kiosks.length === 0 || chartData.length === 0) {
    return (
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">🌡️</span>
            Temperature History
          </h2>
        </div>
        <div className="empty-state">
          <span className="empty-state-icon">📡</span>
          <p>No temperature history yet. Data accumulates as the kiosk runs (updates every 60s).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">🌡️</span>
          Temperature History
          {latestTemp != null && (
            <span style={{
              marginLeft: '0.75rem', fontSize: '0.85rem', fontWeight: 600,
              color: tempColor, padding: '2px 10px',
              backgroundColor: `${tempColor}22`, borderRadius: '999px',
            }}>
              {latestTemp.toFixed(1)}°C now
            </span>
          )}
        </h2>
        <div className="section-actions">
          {kiosks.length > 1 && (
            <select
              id="temp-kiosk-filter"
              value={selectedKiosk}
              onChange={e => setSelectedKiosk(e.target.value)}
              className="search-input"
              style={{ padding: '6px 10px', cursor: 'pointer' }}
            >
              <option value="all">All Kiosks</option>
              {kiosks.map(k => (
                <option key={k} value={k}>{k.replace('CHILL-RAS-', '')}</option>
              ))}
            </select>
          )}
          <div className="date-filter-group">
            {RANGES.map(r => (
              <button
                key={r.key}
                id={`temp-range-${r.key}`}
                className={`date-filter-btn ${range === r.key ? 'date-filter-btn--active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mini stat cards */}
      {latestTemp != null && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Current', value: `${latestTemp.toFixed(1)}°C`, color: tempColor },
            { label: 'Max', value: `${maxTemp?.toFixed(1)}°C`, color: maxTemp >= TEMP_CRIT ? '#ef4444' : '#f59e0b' },
            { label: 'Min', value: `${minTemp?.toFixed(1)}°C`, color: '#22d3ee' },
          ].map(s => (
            <div key={s.label} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '10px', padding: '0.6rem 1.2rem',
            }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.label}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="gradientTemp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={tempColor} stopOpacity={0.35} />
                <stop offset="95%" stopColor={tempColor} stopOpacity={0}    />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="time"
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={false} tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={false} tickLine={false}
              domain={['auto', 'auto']}
              tickFormatter={v => `${v}°`}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Warning reference lines */}
            <ReferenceLine y={TEMP_WARN} stroke="#f59e0b" strokeDasharray="4 4"
              label={{ value: `${TEMP_WARN}°C`, fill: '#f59e0b', fontSize: 11, position: 'insideTopRight' }} />
            <ReferenceLine y={TEMP_CRIT} stroke="#ef4444" strokeDasharray="4 4"
              label={{ value: `${TEMP_CRIT}°C`, fill: '#ef4444', fontSize: 11, position: 'insideTopRight' }} />
            <Area
              type="monotone"
              dataKey="temp"
              stroke={tempColor}
              strokeWidth={2.5}
              fill="url(#gradientTemp)"
              dot={false}
              activeDot={{ r: 5, stroke: tempColor, strokeWidth: 2, fill: '#0f172a' }}
              name="Temperature"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        <span><span style={{ color: '#f59e0b' }}>━━</span> Warm ({TEMP_WARN}°C)</span>
        <span><span style={{ color: '#ef4444' }}>━━</span> Critical ({TEMP_CRIT}°C)</span>
        <span style={{ marginLeft: 'auto' }}>{chartData.length} readings in selected range</span>
      </div>
    </div>
  );
}
